/**
 * The session's GitHub scope, rendered as additive `environment_context` and
 * `runtime_instructions` contributions to the host's system prompt.
 *
 * A sandbox chat started from the unified composer is bound to an owner (and
 * sometimes a repository) that the agent has no other way to learn: nothing in
 * the working directory or the token names it. Without these facts the agent
 * asks the user which owner to use and retries writes outside the active
 * installation's authorized scope.
 *
 * Scope facts belong in `environment_context`. Guidance for explaining why a
 * public repository can be readable but not writable belongs in
 * `runtime_instructions`, since it directs the agent's response to a failed
 * write without changing or expanding the session's permissions.
 */

/** Section id the host accepts for "where this session runs" content. */
const ENVIRONMENT_CONTEXT_SECTION = 'environment_context'

/** Section id the host accepts for situational behavior guidance. */
const RUNTIME_INSTRUCTIONS_SECTION = 'runtime_instructions'

/** The session's GitHub scope as resolved when the task was created. */
export interface RelaySessionScope {
  /** Owner login the active GitHub App installation is scoped to for writes. */
  owner: string
  /** Repository (name-with-owner) checked out for the session, when there is one. */
  repositoryNwo?: string
}

/** An additive system-prompt contribution, matching `config.systemMessageSections`. */
export interface SystemMessageSectionOverride {
  action: 'append'
  content: string
}

function scopeContent({owner, repositoryNwo}: RelaySessionScope): string {
  if (repositoryNwo) {
    return [
      '## Session scope',
      `This session is scoped to the repository \`${repositoryNwo}\`.`,
      `The active GitHub App installation authorizes writes only to \`${repositoryNwo}\` and other granted resources under \`${owner}\`; public repositories outside that installation scope may still be readable.`,
    ].join('\n')
  }
  return [
    '## Session scope',
    `This session is scoped to the GitHub account \`${owner}\` (a user or an organization). No repository is checked out.`,
    `The active GitHub App installation authorizes writes only to granted resources under \`${owner}\`; public repositories outside that installation scope may still be readable.`,
  ].join('\n')
}

const WRITE_ACCESS_GUIDANCE = [
  '## GitHub repository access',
  'Public repositories may be readable even when this session cannot write to them.',
  "If a write to a public repository that this session can read fails because the active GitHub App installation is not authorized for the target repository, explain that the repository is read-only in this session because it is outside the installation's organization or repository scope.",
  "Distinguish the session token's access from the user's own repository permissions. Suggest starting a session scoped to an authorized installation or performing the write manually.",
].join('\n')

/**
 * Build the `config.systemMessageSections` value for {@link scope}, or
 * `undefined` when there is no scope worth stating.
 */
export function buildSessionScopeSections(
  scope: RelaySessionScope | undefined,
): Record<string, SystemMessageSectionOverride> | undefined {
  const owner = scope?.owner.trim()
  if (!owner) return undefined
  const repositoryNwo = scope?.repositoryNwo?.trim()
  return {
    [ENVIRONMENT_CONTEXT_SECTION]: {
      action: 'append',
      content: scopeContent({owner, ...(repositoryNwo ? {repositoryNwo} : {})}),
    },
    [RUNTIME_INSTRUCTIONS_SECTION]: {
      action: 'append',
      content: WRITE_ACCESS_GUIDANCE,
    },
  }
}

/**
 * Resolve the owner the active GitHub App installation is scoped to for writes,
 * from the two shapes the composer produces: a selected repository, or a bare
 * scope login.
 *
 * Returns `undefined` when neither yields a login, so callers can omit the
 * scope rather than assert something they cannot back up.
 */
export function resolveSessionScope(
  repositoryNwo: string | undefined,
  scopeLogin: string | undefined,
): RelaySessionScope | undefined {
  const nwo = repositoryNwo?.trim()
  const owner = nwo?.split('/')[0]?.trim() || scopeLogin?.trim()
  if (!owner) return undefined
  return {owner, ...(nwo ? {repositoryNwo: nwo} : {})}
}
